import { forwardRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { LinkProps } from 'react-router-dom';
import { localeFromPath, buildLocalizedPath } from '@/lib/locale-url';

/**
 * Drop-in replacement for react-router-dom's <Link> that prepends /fr
 * to the `to` prop when the user is currently on a /fr/* URL. Absolute
 * URLs, anchors, and external hrefs pass through unchanged.
 *
 * This means internal-navigation components (Navigation, Footer, hero
 * CTAs) don't need to know about language — they link to the English
 * path and LocalizedLink keeps the visitor in their chosen language.
 */
const LocalizedLink = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ to, ...rest }, ref) => {
    const location = useLocation();

    // Only transform string paths that start with /. External links
    // (http/https/mailto/tel), anchors (#...), and `to` objects with
    // explicit pathname keys are left to the caller.
    if (typeof to !== 'string' || !to.startsWith('/')) {
      return <Link ref={ref} to={to} {...rest} />;
    }

    const locale = localeFromPath(location.pathname);
    const localized = buildLocalizedPath(locale, to);

    return <Link ref={ref} to={localized} {...rest} />;
  },
);

LocalizedLink.displayName = 'LocalizedLink';

export default LocalizedLink;
